import usePrefix from '@/hooks/usePrefix';
import { Button, Input } from '@/components/Bicester';
import React from 'react';
import { ControlLabel, FormGroup } from 'rsuite';
import { getPrice } from '@/components/utils';
import { FormControl } from '@/components/formik-rsuite';
import options from '@/utils/options';
import { BicesterShoppingVillage, Level } from '@/constants';
import { InputProps } from 'rsuite5';
import { FORM_DEFAULT_DISPLAY } from '@/constants/config';
import { PopoverWrapper } from '../PopoverWrapper';

/**
 * input输入数据类型
 */
export type InputType = 'number' | 'textarea' | 'price' | 'level' | 'village' | 'map';

export interface CardInputProps {
  /**
   * 输入框style
   */
  inputStyle?: React.CSSProperties;
  /**
   * 编辑状态
   */
  isEdit?: boolean;
  /**
   * true为不可编辑，可废弃，用isEdit=false代替
   */
  notEditable?: boolean;
  /**
   * 是否必填
   */
  required?: boolean;
  /**
   * 输入框前缀
   */
  inputPrefix?: string;
  label?: string;
  /**
   * 与field.name一致
   * - 只读数据中也要传个key，否则accepter报错
   */
  formKey: string;
  /**
   * field.value为空时显示的value，一般用于只读数据
   */
  value?: React.ReactNode;
  /**
   * value空值时显示 默认是/
   */
  defaultValue?: string | number;
  /**
   * 输入数据类型
   */
  inputType?: InputType;
  /**
   * map索引对象（输入数据类型为map时）
   */
  map?: any;
  /**
   * 是否可以一键清除数据
   */
  cleanable?: boolean;
  /**
   * 一键清除数据触发的方法
   */
  onClean?: () => void;
  /**
   * 输入框宽度占满变为auto
   */
  autoWidth?: boolean;
  /**
   * 是否有按钮
   */
  hasBtn?: boolean;
  /**
   * 按钮显示状态
   */
  showBtn?: boolean;
  /**
   * 是否禁用按钮
   */
  btnDisabled?: boolean;
  /**
   * 按钮的loading状态
   */
  btnLoading?: boolean;
  /**
   * 按钮的label
   */
  btnLabel?: React.ReactNode;
  /**
   * 自定义按钮
   */
  customBtn?: React.ReactNode;
  /**
   * 按钮点击事件
   */
  onClickBtn?: () => void;
  /**
   * label部分的flex占比
   */
  labelWeight?: number;
  /**
   * content部分的flex占比
   */
  contentWeight?: number;
  /**
   * content部分只读时overflow
   * - like-textarea (默认)跟textarea一样换行最多显示三行
   * - nowrap 不换行
   * - ellipsis 省略号显示
   */
  contentOverflow?: 'like-textarea' | 'nowrap' | 'ellipsis' | 'ellipsis-popover';
}

const BicesterCardInput = ({
  className,
  inputStyle,
  isEdit = true,
  notEditable = false,
  required = false,
  inputPrefix,
  label,
  formKey,
  value,
  defaultValue = FORM_DEFAULT_DISPLAY,
  inputType,
  map,
  cleanable,
  onClean,
  autoWidth = false,
  hasBtn = false,
  showBtn = true,
  btnDisabled,
  btnLoading,
  btnLabel,
  customBtn,
  onClickBtn,
  labelWeight,
  contentWeight,
  contentOverflow = 'like-textarea',
  ...props
}: CardInputProps & Omit<InputProps, 'form'>) => {
  const { prefix, classname } = usePrefix('bicester-card-item');

  return (
    <div className={`bicester-card-item-wrap ${className ?? ''}`}>
      <FormGroup
        className={`${classname} bicester-card-${inputType === 'textarea' ? inputType : 'input'}${
          inputType === 'textarea' && isEdit && !notEditable ? ' bicester-card-textarea-isedit' : ''
        }${autoWidth ? ' auto-width' : ''} card-contentoverflow-${contentOverflow}`}
      >
        {label && (
          <ControlLabel
            className={prefix('label')}
            style={{ ...(labelWeight && { flex: labelWeight }) }}
          >
            <div className={prefix('label-text')}>{label}</div>
            <div
              className={`${prefix('label-divider')} ${
                isEdit && !notEditable && required ? 'required' : ''
              }`}
            ></div>
          </ControlLabel>
        )}
        <div
          className={`${prefix('content-wrap')} contentoverflow-${contentOverflow} ${
            isEdit && !notEditable ? prefix('content-wrap-isedit') : ''
          }`}
          style={{ ...(contentWeight && { flex: contentWeight }) }}
        >
          {isEdit && !notEditable ? (
            <FormControl
              className={prefix('content')}
              style={inputStyle}
              name={formKey}
              accepter={Input}
              inputPrefix={inputPrefix}
              {...(inputType === 'textarea' && { inputAs: 'textarea' })}
              type={inputType === 'price' ? 'number' : inputType}
              cleanable={cleanable}
              onClean={onClean}
              {...props}
            />
          ) : (
            <FormControl
              name={formKey}
              accepter={({ field }) => {
                const valText = options(
                  {
                    textarea: () => field.value || value || defaultValue,
                    price: () => getPrice(field.value || value, defaultValue),
                    level: () => Level[field.value || value],
                    village: () => BicesterShoppingVillage[field.value || value],
                    number: () => field.value || value || defaultValue,
                    map: () => map[field.value] || value || defaultValue,
                    default: () => field.value || value || defaultValue,
                  },
                  inputType
                );
                return contentOverflow === 'ellipsis-popover' && valText !== defaultValue ? (
                  <PopoverWrapper popover={{ content: valText }}>
                    <div className={prefix('content-wrap-text')}>{valText}</div>
                  </PopoverWrapper>
                ) : (
                  <div className={prefix('content-wrap-text')}>{valText}</div>
                );
              }}
            />
          )}
          {hasBtn && showBtn && (
            <div className="h-[47px] flex items-center">
              {customBtn || (
                <Button
                  type="submit"
                  className={prefix('content-btn')}
                  disabled={btnDisabled}
                  loading={btnLoading}
                  onClick={onClickBtn}
                >
                  {btnLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </FormGroup>
    </div>
  );
};

export default BicesterCardInput;
